// Card payment step (Italy) — copy approved by the Italian CEO.
//
// One page with two options, mirroring the approved layout:
//   1. "Pagamento automatico" (recommended) → opens the Stripe drop-in inline
//   2. "Pagamento tramite link" (not recommended) → explicit opt-out checkbox
//
// The step cannot be skipped, but it is a CHOICE, not a forced card save:
// "Conferma e continua" unlocks once the patient has either saved a method or
// ticked the opt-out. The drop-in itself is minted only when they ask for it —
// Billing Request/Checkout sessions are single-use, so we don't create one for
// patients who opt out.
import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FormNavigation } from '../components/FormNavigation'
import { Checkbox } from '../components/Checkbox'
import { Button } from '../components/Button'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTenant } from '../context/TenantContext'
import { cardSetupService } from '../services/api'
import { logFlowEvent } from '../services/flowEventService'
import { reportError } from '../services/errorReporter'
import { logger } from '../utils/logger'
import { toast } from 'sonner'
import { CheckCircle2, Loader2, Lock, MessageCircle } from 'lucide-react'
import type { StepProps } from '../types'

interface DropinMessage {
  source?: string
  type?: string
  height?: number
  already?: boolean
  error?: string
}

export function CardPaymentStep({ onNext, onBack }: StepProps) {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const { patientNumber, cardSaved, setCardSaved, cardOptOut, setCardOptOut } = useOnboardingStore()

  const [minting, setMinting] = useState(false)
  const [embedUrl, setEmbedUrl] = useState<string | null>(null)
  const [origin, setOrigin] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [frameHeight, setFrameHeight] = useState(260)
  const openedRef = useRef(false)

  const whatsappUrl = tenant?.supportWhatsappUrl || null

  /** Mint on demand — sessions are single-use, so never on mount. */
  const openDropin = useCallback(async () => {
    if (!/^P\d+$/.test(patientNumber)) {
      toast.error(t('cardSave.noPatientNumber'))
      return
    }
    setMinting(true)
    setFailed(null)
    try {
      const result = await cardSetupService.createSession(patientNumber)
      if (result.available && result.embedUrl && result.dropinOrigin) {
        setEmbedUrl(result.embedUrl)
        setOrigin(result.dropinOrigin)
        if (!openedRef.current) {
          openedRef.current = true
          logFlowEvent(patientNumber, 'card_payment', 'step_entered')
        }
        return
      }
      logger.warn('[CardPay] session unavailable:', result.reason)
      setFailed(result.reason || 'unavailable')
      reportError({
        patientNumber,
        errorType: 'card_setup_error',
        errorMessage: `Card session unavailable: ${result.reason}`,
        step: 'card_payment',
        endpoint: '/card-setup/session',
      })
    } catch (err) {
      logger.error('[CardPay] mint failed:', err)
      setFailed('unreachable')
    } finally {
      setMinting(false)
    }
  }, [patientNumber, t])

  // Drop-in → host messages
  useEffect(() => {
    if (!origin) return
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin) return
      const m = e.data as DropinMessage
      if (m?.source !== 'gam-invoice') return

      switch (m.type) {
        case 'gam_card_height':
          if (typeof m.height === 'number' && m.height > 0) setFrameHeight(m.height)
          break
        case 'gam_card_saved':
          logger.log('[CardPay] saved (already=%s)', m.already)
          setCardSaved(true)
          setCardOptOut(false)
          setEmbedUrl(null)
          logFlowEvent(patientNumber, 'card_payment', 'step_completed')
          toast.success(m.already ? t('cardSave.alreadySaved') : t('cardSave.saved'))
          break
        case 'gam_card_error':
          logger.error('[CardPay] drop-in error:', m.error)
          logFlowEvent(patientNumber, 'card_payment', 'error')
          reportError({
            patientNumber,
            errorType: 'card_setup_error',
            errorMessage: `Drop-in error: ${m.error ?? 'unknown'}`,
            step: 'card_payment',
          })
          toast.error(t('cardSave.error'))
          break
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [origin, patientNumber, setCardSaved, setCardOptOut, t])

  // A decision is required — but our own outage must never trap the patient.
  const canContinue = cardSaved || cardOptOut || failed !== null

  const handleOptOut = (checked: boolean) => {
    setCardOptOut(checked)
    if (checked) setEmbedUrl(null)
  }

  return (
    <div className="form-container">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (canContinue) {
            logFlowEvent(patientNumber, 'card_payment', cardSaved ? 'step_completed' : 'step_entered')
            onNext()
          }
        }}
        className="form-inner py-12"
      >
        <h2 className="text-h2 text-foreground mb-6">{t('cardPay.pageTitle')}</h2>

        {/* ---- Automatic payment (recommended) ---- */}
        {/* Both option headings share the same size/hierarchy by request */}
        <h3 className="text-h3 text-foreground mb-1">{t('cardPay.autoTitle')}</h3>
        <p className="text-body text-primary font-medium mb-4">{t('cardPay.autoSubtitle')}</p>

        <div className={`bg-white rounded-card border p-5 mb-6 ${cardOptOut ? 'border-border opacity-60' : 'border-primary/40'}`}>
          {cardSaved ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
              <p className="text-body text-foreground">{t('cardSave.saved')}</p>
            </div>
          ) : (
            <>
              <h3 className="text-body font-medium text-foreground mb-1">{t('cardPay.whyTitle')}</h3>
              <p className="text-small text-muted-foreground mb-4">{t('cardPay.whyText')}</p>

              <h3 className="text-body font-medium text-foreground mb-2">{t('cardPay.benefitsTitle')}</h3>
              <ul className="space-y-1.5 mb-4">
                {(['Convenient', 'OnTime', 'Reliable'] as const).map((k) => (
                  <li key={k} className="text-small text-foreground flex gap-2">
                    <span className="text-green-600 flex-shrink-0">✓</span>
                    <span>
                      <strong>{t(`cardPay.benefit${k}`)}:</strong> {t(`cardPay.benefit${k}Text`)}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="text-small text-foreground mb-3">
                <strong>{t('cardPay.importantLabel')}</strong> {t('cardPay.importantText')}
              </p>

              <p className="text-small text-muted-foreground flex items-start gap-2 mb-4">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{t('cardPay.stripeNote')}</span>
              </p>

              {embedUrl ? (
                <div className="rounded-card border border-border overflow-hidden">
                  <iframe
                    src={embedUrl}
                    title={t('cardPay.chooseMethod')}
                    height={frameHeight}
                    style={{ width: '100%', border: 0, display: 'block' }}
                    allow="payment *"
                  />
                </div>
              ) : failed ? (
                <div className="text-small text-muted-foreground">
                  <p className="mb-2">
                    {failed === 'patient_not_synced' ? t('cardSave.notSyncedYet') : t('cardSave.unavailable')}
                  </p>
                  <Button type="button" variant="secondary" onClick={() => void openDropin()}>
                    {t('cardSave.retry')}
                  </Button>
                </div>
              ) : (
                <Button type="button" onClick={() => void openDropin()} disabled={minting || cardOptOut}>
                  {minting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
                    </span>
                  ) : (
                    t('cardPay.chooseMethod')
                  )}
                </Button>
              )}
            </>
          )}
        </div>

        {/* ---- Payment by link (opt-out) ---- */}
        {!cardSaved && (
          <>
          <h3 className="text-h3 text-foreground mb-1">{t('cardPay.linkTitle')}</h3>
          <p className="text-body text-muted-foreground mb-4">{t('cardPay.linkSubtitle')}</p>
          <div className="bg-white rounded-card border border-border p-5 mb-4">
            <p className="text-small text-foreground mb-2">{t('cardPay.linkText1')}</p>
            <p className="text-small text-foreground mb-4">{t('cardPay.linkText2')}</p>
            <Checkbox
              id="cardOptOut"
              checked={cardOptOut}
              onChange={(e) => handleOptOut(e.target.checked)}
              label={<span className="font-medium">{t('cardPay.optOut')}</span>}
            />
          </div>
          </>
        )}

        {/* ---- Support ---- */}
        {whatsappUrl && (
          <div className="bg-white rounded-card border border-border p-5">
            <h3 className="text-body font-medium text-foreground mb-1">{t('cardPay.helpTitle')}</h3>
            <p className="text-small text-muted-foreground mb-3">{t('cardPay.helpText')}</p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-small text-primary underline hover:no-underline"
            >
              <MessageCircle className="w-4 h-4" />
              {t('cardPay.whatsapp')}
            </a>
          </div>
        )}

        <FormNavigation onBack={onBack} nextDisabled={!canContinue} nextLabel={t('cardPay.confirm')} />
      </form>
    </div>
  )
}
